import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Form,
  Modal,
} from "react-bootstrap";
import {
  FaClock,
  FaVideo,
  FaShare,
  FaWhatsapp,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaLink,
  FaCheckCircle,
  FaYoutube,
  FaArrowLeft,
  FaStar,
  FaUser,
  FaCertificate,
  FaRegClock,
  FaCalendarAlt,
  FaCheck,
  FaGraduationCap,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import WebinarService from "../../services/webinarService";
import "../../styles/webinar-details.css";

const WebinarDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [registered, setRegistered] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    loadWebinar();
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (webinar && webinar.status === "upcoming") {
      const targetDate = new Date(
        `${webinar.webinar_date}T${webinar.webinar_time}`,
      ).getTime();
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const distance = targetDate - now;

        if (distance < 0) {
          clearInterval(interval);
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        } else {
          setTimeLeft({
            days: Math.floor(distance / (1000 * 60 * 60 * 24)),
            hours: Math.floor(
              (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
            ),
            minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((distance % (1000 * 60)) / 1000),
          });
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [webinar]);

  const loadWebinar = async () => {
    try {
      setLoading(true);
      const response = await WebinarService.getWebinarBySlug(slug);
      setWebinar(response.data);
    } catch (error) {
      console.error("Failed to load webinar:", error);
      navigate("/webinars");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      await WebinarService.registerForWebinar(webinar.id, formData);
      setRegistered(true);
      setShowRegisterModal(false);
      setFormData({ name: "", email: "", phone: "" });
    } catch (error) {
      alert(error.response?.data?.message || "Registration failed");
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const shareUrls = {
    whatsapp: `https://wa.me/?text=Check out this free webinar: ${webinar?.title} - ${window.location.href}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`,
    twitter: `https://twitter.com/intent/tweet?text=${webinar?.title}&url=${window.location.href}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${webinar?.title}`,
  };

  if (loading) {
    return (
      <div className="webinar-details-wrapper">
        <PublicNavbar />
        <div className="webinar-details-nav-spacer"></div>
        <div className="webinar-details-loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading webinar details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!webinar) return null;

  const isUpcoming = webinar.status === "upcoming";
  const isCompleted = webinar.status === "completed";
  const isLive = webinar.status === "live";
  const hasRecording = isCompleted && webinar.recording_link;

  const parseModules = () => {
    if (!webinar.long_description) return [];
    const modules = [];
    const lines = webinar.long_description.split("\n");
    let currentModule = null;

    for (const line of lines) {
      if (line.trim().startsWith("###")) {
        if (currentModule) modules.push(currentModule);
        currentModule = {
          title: line.trim().replace(/^###\s*/, ""),
          content: "",
        };
      } else if (currentModule) {
        currentModule.content += line + "\n";
      }
    }
    if (currentModule) modules.push(currentModule);
    return modules;
  };

  const modules = parseModules();

  return (
    <div className="webinar-details-wrapper">
      <PublicNavbar />
      <div className="webinar-details-nav-spacer"></div>

      {/* Hero Section */}
      <section className="webinar-details-hero">
        <Container>
          <button
            className="webinar-details-back-btn"
            onClick={() => navigate("/webinars")}
          >
            <FaArrowLeft className="me-2" /> Back to Webinars
          </button>

          <div className="webinar-details-status">
            {isLive && (
              <span className="webinar-details-badge webinar-details-badge-live">
                🔴 LIVE NOW
              </span>
            )}
            {isUpcoming && (
              <span className="webinar-details-badge webinar-details-badge-upcoming">
                📅 UPCOMING WEBINAR
              </span>
            )}
            {isCompleted && (
              <span className="webinar-details-badge webinar-details-badge-completed">
                📹 RECORDING AVAILABLE
              </span>
            )}
          </div>

          <h1 className="webinar-details-title">{webinar.title}</h1>

          <div className="webinar-details-meta">
            <div className="webinar-details-meta-item">
              <FaCalendarAlt /> {formatDate(webinar.webinar_date)}
            </div>
            <div className="webinar-details-meta-item">
              <FaRegClock /> {formatTime(webinar.webinar_time)}
            </div>
            <div className="webinar-details-meta-item">
              <FaClock /> {webinar.duration || "2 hours"}
            </div>
            <div className="webinar-details-meta-item">
              <FaUser /> Hosted by {webinar.instructor_name}
            </div>
          </div>

          {isUpcoming && (
            <div className="webinar-details-countdown">
              <div className="webinar-details-countdown-label">
                Webinar Starts In
              </div>
              <div className="webinar-details-countdown-numbers">
                <div className="webinar-details-countdown-block">
                  <span className="number">{timeLeft.days}</span>
                  <span className="label">Days</span>
                </div>
                <div className="webinar-details-countdown-block">
                  <span className="number">{timeLeft.hours}</span>
                  <span className="label">Hours</span>
                </div>
                <div className="webinar-details-countdown-block">
                  <span className="number">{timeLeft.minutes}</span>
                  <span className="label">Minutes</span>
                </div>
                <div className="webinar-details-countdown-block">
                  <span className="number">{timeLeft.seconds}</span>
                  <span className="label">Seconds</span>
                </div>
              </div>
            </div>
          )}

          <div className="webinar-details-actions">
            {isUpcoming && (
              <button
                className="webinar-details-btn-primary"
                onClick={() => setShowRegisterModal(true)}
              >
                Register Now (Free) <FaArrowLeft className="ms-2" />
              </button>
            )}
            {hasRecording && (
              <button
                className="webinar-details-btn-recording"
                onClick={() => window.open(webinar.recording_link, "_blank")}
              >
                <FaYoutube className="me-2" /> Watch Recording
              </button>
            )}
            {isLive && webinar.meeting_link && (
              <button
                className="webinar-details-btn-live"
                onClick={() => window.open(webinar.meeting_link, "_blank")}
              >
                <FaVideo className="me-2" /> Join Live Now
              </button>
            )}
            <button
              className="webinar-details-btn-share"
              onClick={() => setShowShareModal(true)}
            >
              <FaShare className="me-2" /> Share
            </button>
          </div>
        </Container>
        <div className="webinar-details-hero-wave">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="webinar-details-stats">
        <Container>
          <Row>
            <Col md={3} sm={6}>
              <div className="webinar-details-stat-item">
                <h3>500+</h3>
                <p>Students Placed</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="webinar-details-stat-item">
                <h3>10+</h3>
                <p>Years Experience</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="webinar-details-stat-item">
                <h3>90%</h3>
                <p>Placement Rate</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="webinar-details-stat-item">
                <h3>4.9</h3>
                <p>Student Rating</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content */}
      <Container className="webinar-details-content">
        <Row>
          <Col lg={8}>
            {modules.length > 0 && (
              <div className="webinar-details-modules">
                <h2>What You'll Learn</h2>
                <div className="webinar-details-modules-list">
                  {modules.map((module, index) => (
                    <div key={index} className="webinar-details-module-item">
                      <div className="webinar-details-module-number">
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <div className="webinar-details-module-content">
                        <h4>{module.title}</h4>
                        <div
                          dangerouslySetInnerHTML={{ __html: module.content }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="webinar-details-about">
              <h2>About This Webinar</h2>
              <p>{webinar.short_description}</p>
              {webinar.long_description && !modules.length && (
                <div
                  dangerouslySetInnerHTML={{ __html: webinar.long_description }}
                />
              )}
            </div>

            <div className="webinar-details-audience">
              <h2>Is This For You?</h2>
              <Row>
                <Col md={6}>
                  <ul className="webinar-details-audience-list">
                    <li>
                      <FaCheck className="webinar-details-check-icon" /> Final
                      year engineering/BCA/BSc students
                    </li>
                    <li>
                      <FaCheck className="webinar-details-check-icon" /> Recent
                      graduates looking for IT jobs
                    </li>
                    <li>
                      <FaCheck className="webinar-details-check-icon" /> Working
                      professionals wanting to switch to IT
                    </li>
                  </ul>
                </Col>
                <Col md={6}>
                  <ul className="webinar-details-audience-list">
                    <li>
                      <FaCheck className="webinar-details-check-icon" /> Anyone
                      preparing for campus placements
                    </li>
                    <li>
                      <FaCheck className="webinar-details-check-icon" />{" "}
                      Self-taught programmers seeking formal recognition
                    </li>
                    <li>
                      <FaCheck className="webinar-details-check-icon" /> Career
                      changers entering the IT industry
                    </li>
                  </ul>
                </Col>
              </Row>
            </div>
          </Col>

          <Col lg={4}>
            <div className="webinar-details-instructor">
              <h3>Meet Your Instructor</h3>
              <div className="webinar-details-instructor-info">
                {webinar.instructor_image ? (
                  <img
                    src={webinar.instructor_image}
                    alt={webinar.instructor_name}
                    className="webinar-details-instructor-img"
                  />
                ) : (
                  <div className="webinar-details-instructor-placeholder">
                    <FaUser size={40} />
                  </div>
                )}
                <h4>{webinar.instructor_name}</h4>
                {webinar.instructor_bio && <p>{webinar.instructor_bio}</p>}
                <div className="webinar-details-instructor-stats">
                  <span>
                    <FaGraduationCap /> 10,000+ Students
                  </span>
                  <span>
                    <FaStar /> 4.9 Rating
                  </span>
                  <span>
                    <FaCertificate /> Certified
                  </span>
                </div>
              </div>
            </div>

            <div className="webinar-details-info-card">
              <h3>Webinar Details</h3>
              <div className="webinar-details-info-row">
                <span className="label">📅 Date</span>
                <span className="value">
                  {formatDate(webinar.webinar_date)}
                </span>
              </div>
              <div className="webinar-details-info-row">
                <span className="label">⏰ Time</span>
                <span className="value">
                  {formatTime(webinar.webinar_time)}
                </span>
              </div>
              <div className="webinar-details-info-row">
                <span className="label">⏱️ Duration</span>
                <span className="value">{webinar.duration || "2 hours"}</span>
              </div>
              <div className="webinar-details-info-row">
                <span className="label">🎓 Instructor</span>
                <span className="value">{webinar.instructor_name}</span>
              </div>
              <div className="webinar-details-info-row">
                <span className="label">💻 Platform</span>
                <span className="value">Zoom / Google Meet</span>
              </div>
            </div>

            <div className="webinar-details-bonus">
              <div className="webinar-details-bonus-icon">🎁</div>
              <h4>What You'll Get</h4>
              <ul>
                <li>✓ Free Study Materials</li>
                <li>✓ Participation Certificate</li>
                <li>✓ Live Q&A Access</li>
                <li>✓ Recording Access</li>
                <li>✓ Networking Opportunities</li>
              </ul>
            </div>

            {isUpcoming && (
              <div className="webinar-details-sticky-cta">
                <button
                  className="webinar-details-sticky-btn"
                  onClick={() => setShowRegisterModal(true)}
                >
                  Register for Free
                </button>
                <p className="text-center text-muted small mt-2">
                  Limited seats available!
                </p>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Registration Modal */}
      <Modal
        show={showRegisterModal}
        onHide={() => setShowRegisterModal(false)}
        centered
        className="webinar-details-register-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Register for Webinar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleRegister}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name *</Form.Label>
              <Form.Control
                type="text"
                required
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                required
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter your phone number (optional)"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </Form.Group>
            <small className="text-muted d-block mb-3">
              By registering, you'll receive webinar access link and reminders.
            </small>
            <Button type="submit" variant="success" className="w-100">
              Complete Registration
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Success Modal */}
      <Modal
        show={registered}
        onHide={() => setRegistered(false)}
        centered
        className="webinar-details-success-modal"
      >
        <Modal.Body className="text-center py-4">
          <FaCheckCircle size={70} className="text-success mb-3" />
          <h4>Registration Successful! 🎉</h4>
          <p>
            You're registered for <strong>{webinar.title}</strong>
          </p>
          <p className="text-muted">
            We've sent the webinar details to your email.
          </p>
          <Button variant="primary" onClick={() => setRegistered(false)}>
            Close
          </Button>
        </Modal.Body>
      </Modal>

      {/* Share Modal */}
      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        centered
        className="webinar-details-share-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Share this Webinar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="webinar-details-share-buttons">
            <Button variant="success" href={shareUrls.whatsapp} target="_blank">
              <FaWhatsapp className="me-2" /> WhatsApp
            </Button>
            <Button variant="primary" href={shareUrls.facebook} target="_blank">
              <FaFacebook className="me-2" /> Facebook
            </Button>
            <Button variant="info" href={shareUrls.twitter} target="_blank">
              <FaTwitter className="me-2" /> Twitter
            </Button>
            <Button
              variant="secondary"
              href={shareUrls.linkedin}
              target="_blank"
            >
              <FaLinkedin className="me-2" /> LinkedIn
            </Button>
            <Button
              variant="dark"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert("Link copied!");
              }}
            >
              <FaLink className="me-2" /> Copy Link
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />
    </div>
  );
};

export default WebinarDetails;
